import React, { useState } from 'react'
import styled from 'styled-components'
import { Flex, Box, Text, Absolute, Relative, Tooltip } from 'pcln-design-system'
import PropTypes from 'prop-types'
import AirlineImage from '../AirlineImage/AirlineImage'
import StopsDisplay from './StopsDisplay'
import MultipleAirlinesIconWithTooltip from '../MultipleAirlinesIconWithTooltip/MultipleAirlinesIconWithTooltip'

const Wrapper = styled(Relative)`
  display: flex;
  justify-content: space-evenly;
`

const StyledToolTip = styled(Tooltip)`
  white-space: nowrap;
`

const NoWrapText = styled(Text)`
  white-space: nowrap;
  border-radius: 10px;
`

const Line = styled(Absolute)`
  height: 2px;
  width: 100%;
`

const ImageWrapper = styled(Flex)`
  align-items: flex-end;
  height: 36px;
  width: 44px;
`

const AirlineWrapper = styled(Flex)`
  ${(props) => props.theme.mediaQueries.sm} {
    margin: 0 12px 0 12px;
  }
`

const StyledAirlineImage = styled(AirlineImage)`
  width: 32px;
  max-height: 36px;
`

const AirlineText = styled(Text)`
  ${(props) => props.theme.mediaQueries.sm} {
    padding-top: 4px;
  }
`

const ToggleDepartToolTip = (bool, toggleDepartToolTip) => () => {
  toggleDepartToolTip(bool)
}

const ToggleArriveToolTip = (bool, toggleArriveToolTip) => () => {
  toggleArriveToolTip(bool)
}

const SliceDisplay = ({
  layoverInfo,
  departing,
  arrival,
  airline,
  isDepartureSummary,
  altDateText,
  airlineTestId,
  hideStops,
  showAirportToolTip,
  isMobile,
  disableLayoverTooltip,
  isMultipleAirlinesLogoVariant,
  multipleAirlineText,
  ...props
}) => {
  const [showDepartToolTip, toggleDepartToolTip] = useState(false)
  const [showArriveToolTip, toggleArriveToolTip] = useState(false)
  const isMultipleAirlinesLogo = isMultipleAirlinesLogoVariant && multipleAirlineText

  /* istanbul ignore next */
  const stopPropagation = (e) => isMobile && e.stopPropagation()

  const textColor = isDepartureSummary ? 'text.base' : 'text.light'

  /* istanbul ignore next */
  const flightRedesignTextColor = isDepartureSummary ? 'text.light' : 'text.base'

  return (
    <Flex {...props}>
      {airline && (
        <AirlineWrapper flexDirection='column' alignItems='center' mr={[1, '6px']}>
          <ImageWrapper justifyContent='center'>
            <Box>
              {isMultipleAirlinesLogo ? (
                <MultipleAirlinesIconWithTooltip multipleAirlineText={multipleAirlineText} />
              ) : (
                <StyledAirlineImage airlineName={airline.name} airlineLogo={airline.image} />
              )}
            </Box>
          </ImageWrapper>
          <AirlineText
            fontSize={'10px'}
            textAlign='center'
            color={textColor}
            mt={1}
            data-testid={airlineTestId}
          >
            {isMultipleAirlinesLogo ? 'Multiple' : airline.name}
          </AirlineText>
        </AirlineWrapper>
      )}

      <Flex flexDirection='column' width={1}>
        {(altDateText || (departing?.showOriginDate && departing?.dateRange)) && (
          <Flex mb={1}>
            {altDateText && (
              <NoWrapText
                fontSize='10px'
                textAlign='center'
                bg='alert.light'
                px={1}
                mr={['-20px', 0]}
                data-testid='takeoff-date'
              >
                {altDateText}
              </NoWrapText>
            )}
            {departing?.showOriginDate && departing?.dateRange && (
              <NoWrapText fontSize='10px' textAlign='center' p='1px' data-testid='takeoff-date'>
                {departing?.dateRange}
              </NoWrapText>
            )}
          </Flex>
        )}
        <Flex>
          <Box
            onMouseEnter={ToggleDepartToolTip(true, toggleDepartToolTip)}
            onMouseLeave={ToggleDepartToolTip(false, toggleDepartToolTip)}
            // onClick prevents tooltip click from selecting listing
            onClick={stopPropagation}
            data-testid='departure-box'
          >
            {showDepartToolTip && showAirportToolTip && departing?.airportName && (
              <StyledToolTip bg='background.darkest' color='text.lightest' zIndex={1} top center>
                {departing.airportName}
              </StyledToolTip>
            )}
            <Text
              bold
              fontSize={['14px', '16px']}
              mb={1}
              textAlign='right'
              data-testid='departure-time'
              color={flightRedesignTextColor}
            >
              {departing.time}
            </Text>
            <Text fontSize={0} textAlign='right'>
              <Text.span
                bg={/* istanbul ignore next */ departing.isAltAirport && 'alert.light'}
                p='1px'
                data-testid='departure-airport'
                color={flightRedesignTextColor}
              >
                {departing.airport}
              </Text.span>
            </Text>
          </Box>
          <Wrapper width={1} ml={2} mr={2}>
            <Line bg='border.base' top={20} color={flightRedesignTextColor} />
            <Flex width={1} justifyContent='space-around'>
              {layoverInfo.map((layover, index) => (
                <StopsDisplay
                  key={index}
                  location={layover?.airport?.code}
                  stopTitle={layover?.airport?.name}
                  duration={layover?.duration}
                  // textColor={textColor}
                  textColor={flightRedesignTextColor}
                  hideStops={hideStops}
                  enableTooltip={!disableLayoverTooltip}
                  isHideDurationSm={isDepartureSummary}
                />
              ))}
            </Flex>
          </Wrapper>
          <Box
            onMouseEnter={ToggleArriveToolTip(true, toggleArriveToolTip)}
            onMouseLeave={ToggleArriveToolTip(false, toggleArriveToolTip)}
            // onClick prevents tooltip click from selecting listing
            onClick={stopPropagation}
            data-testid='arrival-box'
            minWidth='64px'
          >
            {showArriveToolTip && showAirportToolTip && (
              <StyledToolTip
                data-testid='arrival-tooltip'
                bg='background.darkest'
                color='text.lightest'
                zIndex={1}
                top
                center
              >
                {arrival.airportName}
              </StyledToolTip>
            )}
            <Flex mb={1}>
              <Text
                bold
                fontSize={['14px', '16px']}
                data-testid='arrival-time'
                color={flightRedesignTextColor}
              >
                {arrival.time}
              </Text>
              {arrival.overnightDays && (
                <Text fontSize={0} ml='2px' data-testid='overnight-days' color={flightRedesignTextColor}>
                  +{arrival.overnightDays}
                </Text>
              )}
            </Flex>
            <Text fontSize={0} width='40px'>
              <Text.span
                bg={arrival.isAltAirport && 'alert.light'}
                p='1px'
                data-testid='arrival-airport'
                color={flightRedesignTextColor}
              >
                {arrival.airport}
              </Text.span>
            </Text>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  )
}

SliceDisplay.defaultProps = {
  layoverInfo: [],
}

SliceDisplay.propTypes = {
  /** List of layover durations and locations  */
  layoverInfo: PropTypes.arrayOf(PropTypes.object),

  /** Departing data containing time and airport */
  departing: PropTypes.shape({
    time: PropTypes.string,
    airport: PropTypes.string,
    airportName: PropTypes.string,
    isAltAirport: PropTypes.bool,
    // SETI for show original dates when user selected alt date
    showOriginDate: PropTypes.bool,
    dateRange: PropTypes.string,
  }),

  /** Arrival data containing time and airport */
  arrival: PropTypes.shape({
    time: PropTypes.string,
    airport: PropTypes.string,
    airportName: PropTypes.string,
    overnightDays: PropTypes.number,
    isAltAirport: PropTypes.bool,
  }),

  /** Airline data for logo and airline name */
  airline: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string,
  }),

  /** if is used within DepartureSummary component */
  isDepartureSummary: PropTypes.bool,

  /* Alt Date string to display alt date above time */
  altDateText: PropTypes.string,

  /** Test identifier for airline */
  airlineTestId: PropTypes.string,

  /** Hide stops at smaller breakpoints */
  hideStops: PropTypes.bool,

  /** SETI for displaying airport tooltip */
  showAirportToolTip: PropTypes.bool,

  /** Bool for determining if mobile user */
  isMobile: PropTypes.bool,

  /** Disable the tooltips on the layovers */
  disableLayoverTooltip: PropTypes.bool,

  isMultipleAirlinesLogoVariant: PropTypes.bool,

  multipleAirlineText: PropTypes.string,
}

export default SliceDisplay
